import { Component, DestroyRef, OnInit, signal } from '@angular/core';
import { faTools } from '@fortawesome/free-solid-svg-icons';
import { PhysicalInventoryService } from '../../services/physical-inventory.service';
import {
    ConfigConstants,
    ConnectivityService,
    DateUtilities,
    DateTimeUtilities,
    isDefined,
    DeviceLockService,
} from '@evorait/pace-core';
import { distinctUntilChanged, filter, map, mergeMap, tap } from 'rxjs/operators';
import { EMPTY, merge, Observable, of, Subject, switchMap } from 'rxjs';
import { PiScheduleDetails } from '../../class/pi-schedule';
import { PhysicalInventoryModalService } from '../../services/physical-inventory-modal.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import PI_ALERT_TYPE = ConfigConstants.PI_ALERT_TYPE;
import EVENT_TYPE = ConfigConstants.EVENT_TYPE;
import { PhysicalInventoryStorageService } from '../../services/physical-inventory-storage.service';

@Component({
    selector: 'pace-physical-inventory-alert',
    templateUrl: './physical-inventory-alert.component.html',
    standalone: false,
})
export class PhysicalInventoryAlertComponent implements OnInit {
    public faTools = faTools;
    private userActionSubject: Subject<void> = new Subject();
    public scheduleDateAndTime = signal('');
    public isScheduleAfterNextWorkingDay = signal(false);

    public constructor(
        private readonly physicalInventoryService: PhysicalInventoryService,
        private readonly physicalInventoryModalService: PhysicalInventoryModalService,
        private readonly connectivityService: ConnectivityService,
        private readonly destroyRef: DestroyRef,
        private readonly physicalInventoryStorageService: PhysicalInventoryStorageService,
        private readonly deviceLockService: DeviceLockService,
    ) {}

    public ngOnInit(): void {
        const piScheduleEnabled$ = this.physicalInventoryService.activePISchedule$.pipe(map(() => EVENT_TYPE.Sync));

        const isOnline$ = this.connectivityService.isOnline$.pipe(
            distinctUntilChanged(),
            tap((isOnline) => {
                if (isOnline) {
                    this.physicalInventoryService.checkAndClearOldOfflineAlertTimer();
                }
            }),
            filter((isOnline) => !isOnline),
            map(() => EVENT_TYPE.Offline),
        );

        const userAction$ = this.userActionSubject.pipe(map(() => EVENT_TYPE.UserClick));

        const events$ = merge(userAction$, piScheduleEnabled$, isOnline$);

        events$
            .pipe(
                filter(() => this.deviceLockService.isLockActive()),
                switchMap((event) =>
                    this.physicalInventoryStorageService.getPIScheduleDetails().pipe(
                        filter(isDefined),
                        map((schedule) => ({ event, schedule })),
                    ),
                ),
                mergeMap(({ event, schedule }) => {
                    // PI timings need to be handled in utc. ticket: https://evorait.atlassian.net/browse/SHP-9864
                    const now = moment.utc();

                    this.updateTemplateStrings(now, schedule);

                    const isDateInRange = DateTimeUtilities.isDateAndTimeInRange(
                        now,
                        schedule.startDateAndTime,
                        schedule.endDateAndTime,
                    );
                    return isDateInRange
                        ? this.showPhysicalInventory(schedule, event)
                        : this.handleAlerts(schedule, event);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    /**
     * Checks for any alert to be shown based on the schedule date and time.
     * If the schedule is in the past, then the re-schedule alert is shown.
     * If the schedule is in the future, then the schedule alert for the future is shown.
     * If the schedule is later today, then the schedule alert for today is shown.
     * If the schedule is tomorrow, then the schedule alert for tomorrow is shown.
     *
     * ticket: https://evorait.atlassian.net/browse/SHP-8422
     *
     * @param schedule  PI schedule
     * @param event type of event that was triggered
     * @returns Observable
     */
    public handleAlerts(schedule: PiScheduleDetails, event: EVENT_TYPE): Observable<void> {
        const alert = this.resolveAlertType(schedule);
        if (alert.isAlertConfirmed && event !== EVENT_TYPE.UserClick) {
            return EMPTY;
        }

        return this.physicalInventoryModalService
            .openPIAlert(alert.alertMessage, alert.isAlertConfirmed, alert.alertType === PI_ALERT_TYPE.NEW_ALERT)
            .pipe(
                switchMap((confirmed) => {
                    if (confirmed && !alert.isAlertConfirmed) {
                        this.physicalInventoryService.checkAndClearOldOfflineAlertTimer();
                        return this.physicalInventoryService.updatePIScheduleDetails(alert.alertType);
                    }
                    if (event === EVENT_TYPE.Offline && !confirmed) {
                        this.physicalInventoryService.setOfflineAlertTimer();
                    }
                    return of(undefined);
                }),
            );
    }

    /**
     * Shows the physical inventory modal if it is available if not shows offline alert.
     *
     * @param schedule latest pi schedule details
     * @param event event type
     * @returns Observable
     */
    private showPhysicalInventory(schedule: PiScheduleDetails, event: EVENT_TYPE): Observable<void> {
        return this.physicalInventoryService.getPhysicalInventoryData().pipe(
            tap((piModel) => {
                if (piModel) {
                    this.physicalInventoryModalService.openPhysicalInventoryModal(piModel);
                }
            }),
            filter(
                (piModel) =>
                    !piModel &&
                    !this.connectivityService.isInternetConnectionAvailable() &&
                    (!schedule.isOfflineAlertConfirmed || event === EVENT_TYPE.UserClick),
            ),
            switchMap(() => {
                const startTime = DateUtilities.formatTime(schedule.startDateAndTime);
                const endTime = DateUtilities.formatTime(schedule.endDateAndTime);

                return this.physicalInventoryModalService.openPIAlert(
                    this.physicalInventoryModalService.getOfflineAlertMessage(startTime, endTime),
                    schedule.isOfflineAlertConfirmed,
                );
            }),
            switchMap((confirmed) => {
                if (confirmed && event !== EVENT_TYPE.UserClick) {
                    this.physicalInventoryService.checkAndClearOldOfflineAlertTimer();
                    return this.physicalInventoryService.updatePIScheduleDetails(PI_ALERT_TYPE.OFFLINE_ALERT);
                }
                this.physicalInventoryService.setOfflineAlertTimer();
                return of(undefined);
            }),
        );
    }

    public updateTemplateStrings(currentDateAndTime: moment.Moment, schedule: PiScheduleDetails): void {
        this.scheduleDateAndTime.set(
            DateUtilities.formatDate(schedule.startDateAndTime) +
                ' | ' +
                DateUtilities.formatTime(schedule.startDateAndTime) +
                ' - ' +
                DateUtilities.formatTime(schedule.endDateAndTime),
        );
        this.isScheduleAfterNextWorkingDay.set(
            DateTimeUtilities.isDateAfterNextWorkingDay(currentDateAndTime, schedule.startDateAndTime, true),
        );
    }

    /**
     * Evaluates the alert type based on the schedule date and time.
     *
     * @param schedule latest pi schedule details
     * @returns object with alert type, isAlertConfirmed and alertMessage values
     */
    private resolveAlertType(schedule: PiScheduleDetails): AlertState {
        // PI timings need to be handled in utc. ticket: https://evorait.atlassian.net/browse/SHP-9864
        const now = moment.utc();
        const date = DateUtilities.formatDate(schedule.startDateAndTime);
        const startTime = DateUtilities.formatTime(schedule.startDateAndTime);
        const endTime = DateUtilities.formatTime(schedule.endDateAndTime);

        if (now.isAfter(schedule.endDateAndTime, 'minute')) {
            return {
                alertType: PI_ALERT_TYPE.RESCHEDULE_ALERT,
                isAlertConfirmed: schedule.isRescheduleAlertConfirmed,
                alertMessage: this.physicalInventoryModalService.getRescheduleAlertMessage(),
            };
        }

        if (schedule.startDateAndTime.isSame(now, 'day')) {
            return {
                alertType: PI_ALERT_TYPE.SCHEDULE_ALERT,
                isAlertConfirmed: schedule.isScheduleAlertConfirmed,
                alertMessage: this.physicalInventoryModalService.getScheduleAlertMessageForToday(startTime, endTime),
            };
        }

        if (DateTimeUtilities.isDateNextWorkingDay(now, schedule.startDateAndTime, true)) {
            return {
                alertType: PI_ALERT_TYPE.SCHEDULE_ALERT,
                isAlertConfirmed: schedule.isScheduleAlertConfirmed,
                alertMessage: this.physicalInventoryModalService.getScheduleAlertMessageForTomorrow(startTime, endTime),
            };
        }

        return {
            alertType: PI_ALERT_TYPE.NEW_ALERT,
            isAlertConfirmed: schedule.isNewScheduleAlertConfirmed,
            alertMessage: this.physicalInventoryModalService.getScheduleAlertMessageForFutureDate(
                date,
                startTime,
                endTime,
            ),
        };
    }

    public onUserClick(): void {
        this.userActionSubject.next();
    }
}

export interface AlertState {
    alertType: PI_ALERT_TYPE;
    isAlertConfirmed: boolean;
    alertMessage: string;
}
